<script lang="ts">
  import { navigate } from 'svelte-routing';
  import { api, ApiError } from '../../lib/api';
  import { user, type User } from '../../lib/stores/auth';
  import { showToast } from '../../lib/stores/toast';
  import { withPageTitle } from '../../lib/page-title';
  import {
    fetchAvailability,
    hintFor,
    normalize,
    type AvailabilityState,
  } from '../../lib/username';
  import Button from '../../components/ui/Button.svelte';
  import Card from '../../components/ui/Card.svelte';
  import Field from '../../components/ui/Field.svelte';
  import Banner from '../../components/ui/Banner.svelte';

  $effect(() => withPageTitle('Create account'));

  // Email/password signup. Username is checked live against
  // /api/auth/username-available with a 350ms debounce so we don't fire
  // a request on every keystroke. The server-side validator is the source
  // of truth — we use the live check only for hint UI; the real verdict
  // happens at submit time and goes through serverError.fieldError().

  const API_BASE: string = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost';

  let username = $state('');
  let email = $state('');
  let password = $state('');
  let passwordConfirm = $state('');
  let submitting = $state(false);
  let serverError = $state<ApiError | null>(null);

  let availability = $state<AvailabilityState>({ status: 'idle' });
  let availabilityToken = 0;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;

  function onUsernameInput(): void {
    if (debounceTimer) clearTimeout(debounceTimer);
    const value = normalize(username);
    if (value !== username) username = value;
    availability = { status: 'checking' };
    const myToken = ++availabilityToken;
    debounceTimer = setTimeout(async () => {
      const next = await fetchAvailability(value);
      // Drop stale responses — user kept typing while we waited.
      if (myToken === availabilityToken) availability = next;
    }, 350);
  }

  const usernameHint = $derived(hintFor(availability));
  const usernameLooksBad = $derived(availability.status === 'unavailable');

  async function submit(event: Event): Promise<void> {
    event.preventDefault();
    if (submitting) return;
    submitting = true;
    serverError = null;
    try {
      const res = await api<{ user: User }>('/api/auth/register', {
        method: 'POST',
        body: JSON.stringify({
          username,
          email,
          password,
          password_confirmation: passwordConfirm,
        }),
      });
      user.set(res.user);
      navigate('/verify-pending', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        serverError = err;
      } else {
        showToast("Couldn't create your account. Please try again.", 'error');
      }
    } finally {
      submitting = false;
    }
  }
</script>

<Card padding="lg" elevated>
  <form class="auth-form" onsubmit={submit} novalidate>
    <header class="auth-form__header">
      <h2 class="auth-form__title">Create your account</h2>
      <p class="auth-form__subtitle">Track your scores. Compete on leaderboards.</p>
    </header>

    {#if serverError && !serverError.errors}
      <Banner variant="error">{serverError.message}</Banner>
    {/if}

    <a class="auth-form__oauth" href="{API_BASE}/auth/google">
      <svg viewBox="0 0 24 24" aria-hidden="true" class="auth-form__oauth-mark">
        <path d="M21.6 12.227c0-.69-.062-1.353-.178-1.99H12v3.764h5.387a4.605 4.605 0 0 1-1.999 3.022v2.51h3.235c1.892-1.745 2.977-4.314 2.977-7.306Z" fill="#4285F4"/>
        <path d="M12 22c2.7 0 4.964-.896 6.619-2.427l-3.235-2.51c-.896.6-2.041.954-3.384.954-2.604 0-4.808-1.76-5.595-4.123H3.072v2.59A9.997 9.997 0 0 0 12 22Z" fill="#34A853"/>
        <path d="M6.405 13.894A6.005 6.005 0 0 1 6.09 12c0-.659.114-1.299.314-1.894V7.516H3.072A9.997 9.997 0 0 0 2 12c0 1.614.387 3.14 1.072 4.484l3.333-2.59Z" fill="#FBBC05"/>
        <path d="M12 5.983c1.47 0 2.788.505 3.825 1.498l2.868-2.867C16.96 2.99 14.696 2 12 2A9.997 9.997 0 0 0 3.072 7.516l3.333 2.59C7.192 7.743 9.396 5.983 12 5.983Z" fill="#EA4335"/>
      </svg>
      Continue with Google
    </a>

    <div class="auth-form__divider"><span>or</span></div>

    <Field
      label="Username"
      name="username"
      autocomplete="username"
      required
      bind:value={username}
      oninput={onUsernameInput}
      hint={usernameHint}
      error={serverError?.fieldError('username') ?? (usernameLooksBad ? 'Not available.' : null)}
    />

    <Field
      label="Email"
      name="email"
      type="email"
      autocomplete="email"
      required
      bind:value={email}
      error={serverError?.fieldError('email')}
    />

    <Field
      label="Password"
      name="password"
      type="password"
      autocomplete="new-password"
      required
      bind:value={password}
      hint="At least 8 characters."
      error={serverError?.fieldError('password')}
    />

    <Field
      label="Confirm password"
      name="password_confirmation"
      type="password"
      autocomplete="new-password"
      required
      bind:value={passwordConfirm}
      error={serverError?.fieldError('password_confirmation')}
    />

    <Button type="submit" variant="primary" size="lg" block loading={submitting}>
      Create account
    </Button>

    <p class="auth-form__alt">
      Already have an account? <a href="/login">Sign in</a>
    </p>
  </form>
</Card>
